import { ref } from 'vue'
import { getDashboardsForRoles, hasMultipleDashboards, getDefaultDashboardForRoles } from '@/utilities/dashboards'
// stores
import { useAuthStore } from '@/stores/AuthStore'
import { useAbility } from '@casl/vue'

export interface NavItem {
  title: string
  route: string
  localeKey: string
  localePluralization: number
  isVisible: boolean
}

interface NavItemParent extends NavItem {
  children: NavItem[] | null
  icon: string
  id?: string
}

export const useNavItems = () => {
  const { can } = useAbility()
  // Type assertion for can() function - it works with 1 arg at runtime
  const canCheck = (permission: string) => (can as (action: string, subject?: unknown, field?: string) => boolean)(permission)
  const sections = ref<NavItemParent[][]>([])
  const authStore = useAuthStore()

  // Main navigation items (top section)
  const mainItems: NavItemParent[] = [
    {
      title: 'Tasks',
      route: '/tasks',
      icon: 'check-double',
      id: 'tasks',
      localeKey: 'ui.entities.task.label',
      localePluralization: 2,
      isVisible: canCheck('view_tasks'),
      children: null
    },
    {
      title: 'Customers',
      route: '/customers',
      icon: 'users',
      localeKey: 'ui.entities.customer',
      localePluralization: 2,
      isVisible: canCheck('view_customers'),
      children: [
        {
          title: 'All',
          route: '/customers',
          localeKey: 'ui.common.all',
          localePluralization: 1,
          isVisible: canCheck('view_customers_list')
        },
        // {
        //   title: 'Flow',
        //   route: '/customers/flow',
        //   icon: 'wind',
        //   localeKey: 'ui.entities.flow.label',
        //   localePluralization: 1,
        //   isVisible: 'view_flow'
        // },
        {
          title: 'Campaigns',
          route: '/customers/campaigns',
          localeKey: 'ui.entities.campaign',
          localePluralization: 2,
          isVisible: canCheck('view_campaigns')
        }
      ]
    },
    {
      title: 'Appointments',
      route: '/appointments',
      icon: 'calendar',
      localeKey: 'ui.entities.appointment.label',
      localePluralization: 2,
      isVisible: canCheck('view_appointments'),
      children: [
        {
          title: 'Manager',
          route: '/appointments/manager',
          localeKey: 'ui.entities.user.role.manager', // TODO: change locale key as this is not the correct one to use
          localePluralization: 1,
          isVisible: canCheck('assign_appointments')
        },
        {
          title: 'Calendar',
          route: '/appointments/calendar',
          localeKey: 'ui.entities.calendar',
          localePluralization: 1,
          isVisible: true
        }
        //  FIX: view broken with api changes, info not currently shown to users
        // {
        //   title: 'Availability',
        //   route: '/appointments/availability',
        //   localeKey: 'ui.entities.availability',
        //   localePluralization: 1,
        //   isVisible: can('view_availability')
        // }
      ]
    },
    {
      title: 'Conversations',
      route: '/conversations',
      icon: 'comments',
      localeKey: 'ui.entities.conversation',
      localePluralization: 2,
      isVisible: canCheck('view_conversations'),
      children: [
        // {
        //   title: 'Inbox',
        //   route: '/inbox',
        //   localeKey: 'ui.entities.inbox',
        //   localePluralization: 1
        // },
        // {
        //   title: 'All Conversations',
        //   route: '/conversations/all',
        //   localePluralization: 1,
        //   localeKey: 'ui.common.all'
        // },
        {
          title: 'Missed Conversations',
          route: '/conversations/missed',
          localePluralization: 1,
          localeKey: 'ui.common.missed',
          isVisible: true
        }
      ]
    },
    {
      title: 'Vehicles',
      route: '/vehicles',
      icon: 'car',
      localeKey: 'ui.entities.vehicle.label',
      localePluralization: 2,
      isVisible: canCheck('view_vehicles'),
      children: [
        {
          title: 'Valuations',
          route: '/vehicles/valuations',
          localeKey: 'ui.entities.valuation',
          localePluralization: 2,
          isVisible: canCheck('view_valuations')
        },
        {
          title: 'ReAuction',
          route: '/vehicles/reauction',
          localeKey: 'ui.entities.reauction.label',
          localePluralization: 1,
          isVisible: true
        },
        // {
        //   title: 'Used Stock',
        //   route: '/vehicles/stock/used',
        //   localeKey: 'ui.entities.usedStock',
        //   localePluralization: 1,
        //   isVisible: can('view_vehicles_used_stock')
        // },
        {
          title: 'Models',
          route: '/vehicles/models',
          localeKey: 'ui.entities.vehicle.model',
          localePluralization: 2,
          isVisible: canCheck('view_vehicle_models')
        }
      ]
    },
    {
      title: 'Reports',
      route: '/reports',
      icon: 'chart-column',
      localeKey: 'ui.entities.report.label',
      localePluralization: 2,
      isVisible: canCheck('view_reports'),
      children: null
    }
    // {
    //   title: 'Invoicing',
    //   route: '/finance/invoicing',
    //   icon: 'file-invoice-dollar',
    //   localeKey: 'ui.entities.finance.invoicing',
    //   localePluralization: 2,
    //   isVisible: 'view_finances'
    //   // children: [
    //   //   {
    //   //     title: 'Dashboard',
    //   //     route: '/finance',
    //   //     localeKey: 'ui.entities.dashboard',
    //   //     localePluralization: 1
    //   //   },
    //   //   {
    //   //     title: 'Invoicing',
    //   //     route: '/finance/invoicing',
    //   //     localeKey: 'ui.entities.finance.invoice',
    //   //     localePluralization: 2
    //   //   }
    //   // ]
    // }
  ]

  // Bottom section items (Settings)
  const bottomItems: NavItemParent[] = [
    {
      title: 'Settings',
      route: '/settings',
      icon: 'gear',
      localeKey: 'ui.entities.setting',
      localePluralization: 2,
      isVisible: canCheck('view_settings'),
      children: null
    }
  ]

  // Convert role strings to numbers for dashboard functions
  const roleIds = authStore.roles.map(role => Number.parseInt(role, 10)).filter(id => !Number.isNaN(id))

  const userHasMultipleDashboards = hasMultipleDashboards(roleIds)

  if (userHasMultipleDashboards) {
    mainItems.unshift({
      title: 'Dashboards',
      route: '/dashboards',
      icon: 'gauge',
      localeKey: 'ui.entities.dashboard',
      localePluralization: 2,
      children: getDashboardsForRoles(roleIds),
      isVisible: true
    })
  } else {
    // Get the single dashboard for the user
    const userDashboards = getDashboardsForRoles(roleIds)
    const singleDashboard = userDashboards.length === 1 ? userDashboards[0] : getDefaultDashboardForRoles(roleIds)

    // If user has exactly one dashboard, link directly to it (non-expandable)
    // Otherwise, use the default dashboard or fallback to '/'
    const dashboardRoute = singleDashboard?.route || '/'

    mainItems.unshift({
      title: 'Home ',
      route: dashboardRoute,
      icon: 'home',
      localeKey: 'ui.common.home',
      localePluralization: 1,
      children: null, // Set to null so it's not expandable
      isVisible: true
    })
  }

  // Filter visible items for each section
  const filteredMainItems = filterVisible(mainItems)
  const filteredBottomItems = filterVisible(bottomItems)

  // Consolidate all sections into a single array
  // Each nested array represents a section
  sections.value = [filteredMainItems, filteredBottomItems]

  return sections.value
}

function filterVisible(items: NavItemParent[]): NavItemParent[] {
  return items
    .filter(item => item.isVisible) // keep only visible ones
    .map(item => {
      // if it has children, filter them (children are NavItem[], not NavItemParent[])
      const children = item.children
        ? item.children.filter(child => child.isVisible)
        : undefined
      return { ...item, ...(children ? { children } : {}) }
    })
}
